import { PRESET_CAPABILITIES } from "./capabilities.js";
import { digestCandidate } from "./hash.js";
import { normalizeOps } from "./normalize.js";
export const POLICY_VERSION = "2026.08.09.1";
export const STATIC_LIMITS = {
    maxModulations: PRESET_CAPABILITIES.limits.maxModulations,
    maxUnisonVoicesTotal: PRESET_CAPABILITIES.limits.maxUnisonVoicesTotal,
    maxVolume: 0.85,
    maxFilterResonance: 0.92,
    maxCustomPoints: PRESET_CAPABILITIES.limits.maxCustomPoints,
    maxSequenceSteps: PRESET_CAPABILITIES.limits.maxSequenceSteps,
    maxSequenceLanes: PRESET_CAPABILITIES.limits.maxSequenceLanes,
    maxEffectFeedback: 0.9,
    maxDistortionDrive: 0.85,
    maxCombinedWet: 2.5,
};
/**
 * Deterministic proposal policy.
 * Approximate preview measurements are advisory; these static limits are the
 * authorization boundary when native render is unavailable.
 */
export function evaluatePolicy(ops, base) {
    const normalized = normalizeOps(ops, base);
    const reasonCodes = [];
    if (!normalized.ok) {
        reasonCodes.push("normalize_failed");
        if (normalized.issues.some((i) => i.code === "resource_collision")) {
            reasonCodes.push("resource_collision");
        }
    }
    const model = normalized.model;
    if (model.modulations.length > STATIC_LIMITS.maxModulations) {
        reasonCodes.push("modulation_limit");
    }
    const unisonTotal = model.params.osc1_unison_voices +
        model.params.osc2_unison_voices +
        model.params.osc3_unison_voices;
    if (unisonTotal > STATIC_LIMITS.maxUnisonVoicesTotal) {
        reasonCodes.push("cpu_hazard");
    }
    if (model.params.volume > STATIC_LIMITS.maxVolume) {
        reasonCodes.push("output_gain_limit");
    }
    if (model.params.filter_resonance > STATIC_LIMITS.maxFilterResonance) {
        reasonCodes.push("envelope_hazard");
    }
    for (const mod of model.modulators) {
        if (mod.shape.kind === "custom" && mod.shape.points.length > STATIC_LIMITS.maxCustomPoints) {
            reasonCodes.push("custom_points_limit");
        }
        if (mod.shape.kind === "steps" && mod.shape.values.length > PRESET_CAPABILITIES.limits.maxStepValues) {
            reasonCodes.push("custom_points_limit");
        }
    }
    for (const seq of model.sequences) {
        if (seq.steps.length > STATIC_LIMITS.maxSequenceSteps) {
            reasonCodes.push("sequence_limit");
        }
        if (seq.lanes.length > STATIC_LIMITS.maxSequenceLanes) {
            reasonCodes.push("sequence_limit");
        }
    }
    const feedbacks = [
        model.params.delay_feedback,
        model.params.chorus_feedback,
        model.params.phaser_feedback,
        model.params.flanger_feedback,
    ];
    if (feedbacks.some((f) => f > STATIC_LIMITS.maxEffectFeedback)) {
        reasonCodes.push("effect_feedback_limit");
    }
    if (model.params.distortion_drive > STATIC_LIMITS.maxDistortionDrive && model.params.distortion_on >= 0.5) {
        reasonCodes.push("effect_feedback_limit");
    }
    const wetSum = (model.params.delay_on >= 0.5 ? model.params.delay_dry_wet : 0) +
        (model.params.reverb_on >= 0.5 ? model.params.reverb_dry_wet : 0) +
        (model.params.chorus_on >= 0.5 ? model.params.chorus_dry_wet : 0) +
        (model.params.phaser_on >= 0.5 ? model.params.phaser_dry_wet : 0) +
        (model.params.flanger_on >= 0.5 ? model.params.flanger_dry_wet : 0) +
        (model.params.distortion_on >= 0.5 ? model.params.distortion_mix : 0);
    if (wetSum > STATIC_LIMITS.maxCombinedWet) {
        reasonCodes.push("effect_feedback_limit");
    }
    // Filter routing contradiction: filter on but no oscillator/sample inputs.
    if (model.params.filter_on >= 0.5 &&
        model.params.filter_osc1_input < 0.5 &&
        model.params.filter_osc2_input < 0.5 &&
        model.params.filter_osc3_input < 0.5 &&
        model.params.filter_sample_input < 0.5 &&
        (model.params.osc1_level > 0 || model.params.osc2_level > 0 || model.params.osc3_level > 0)) {
        // Auto-heal is not policy's job; flag only when destinations also disabled.
        if (model.params.osc1_destination_filter < 0.5 &&
            model.params.osc2_destination_filter < 0.5 &&
            model.params.osc3_destination_filter < 0.5) {
            reasonCodes.push("unsupported_topology");
        }
    }
    const uniqueCodes = [...new Set(reasonCodes)];
    const allowed = uniqueCodes.length === 0;
    if (allowed)
        uniqueCodes.push("ok");
    const candidate = {
        policyVersion: POLICY_VERSION,
        schemaVersion: model.schemaVersion,
        model,
        ops: normalized.ops,
        coercions: normalized.coercions,
    };
    return {
        allowed,
        requiresApproval: false,
        policyVersion: POLICY_VERSION,
        reasonCodes: uniqueCodes,
        candidateDigest: digestCandidate(candidate),
        model,
        coercions: normalized.coercions,
        issues: normalized.issues,
        staticLimits: { ...STATIC_LIMITS },
    };
}
//# sourceMappingURL=policy.js.map